import { Button } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { downloadReportResponse } from "../components/classeur/download-report-response";

type Observation = {
  id: number;
  description: string;
  reportId: number;
  reportNumero: string | null;
  noticeId: number;
  nAvis: string | null;
  requiresResponse: boolean;
  responseDraft: string | null;
};

type NoticeGroup = {
  noticeId: number;
  nAvis: string | null;
  observations: Observation[];
};

type ReportGroup = {
  reportId: number;
  reportNumero: string | null;
  notices: NoticeGroup[];
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; reports: ReportGroup[] };

/** Default per-avis response text: the non-empty observation drafts joined. */
function defaultResponseText(observations: Observation[]): string {
  return observations
    .map((o) => o.responseDraft?.trim())
    .filter((d): d is string => !isNil(d) && d.length > 0)
    .join("\n");
}

function groupByReport(items: Observation[]): ReportGroup[] {
  const byReport = new Map<number, ReportGroup>();
  for (const obs of items) {
    let report = byReport.get(obs.reportId);
    if (isNil(report)) {
      report = {
        reportId: obs.reportId,
        reportNumero: obs.reportNumero,
        notices: [],
      };
      byReport.set(obs.reportId, report);
    }
    let notice = report.notices.find((n) => n.noticeId === obs.noticeId);
    if (isNil(notice)) {
      notice = { noticeId: obs.noticeId, nAvis: obs.nAvis, observations: [] };
      report.notices.push(notice);
    }
    notice.observations.push(obs);
  }
  return [...byReport.values()];
}

/**
 * Screen — "Générer une réponse à rapport d'examen".
 *
 * Lists the observations flagged "appelle une réponse", grouped by exam
 * report then by avis. The rang-1 travaux pilot writes the final response text
 * per avis (pre-filled from the observation drafts), then generates a
 * persisted `report_response` and downloads the resulting artefact (CSV).
 */
export function GenererReponseRapport() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  // Response text keyed by noticeId.
  const [texts, setTexts] = useState<Record<number, string>>({});
  const [submittingReportId, setSubmittingReportId] = useState<number | null>(
    null,
  );
  // Persisted response id keyed by reportId, once generated.
  const [generated, setGenerated] = useState<Record<number, number>>({});

  const fetchObservations = useCallback(async () => {
    if (isNil(yardId)) return;
    setState({ status: "loading" });
    try {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/observations",
        { params: { path: { yardId } } },
      );
      if (error || isNil(data)) {
        setState({
          status: "error",
          error: "Erreur lors du chargement des observations",
        });
        return;
      }
      const items = data.data.items.filter((o) => o.requiresResponse);
      const reports = groupByReport(items);
      setTexts(
        Object.fromEntries(
          reports.flatMap((r) =>
            r.notices.map((n) => [
              n.noticeId,
              defaultResponseText(n.observations),
            ]),
          ),
        ),
      );
      setState({ status: "success", reports });
    } catch {
      setState({
        status: "error",
        error: "Erreur lors du chargement des observations",
      });
    }
  }, [yardId]);

  useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  const generate = async (report: ReportGroup) => {
    if (isNil(yardId)) return;
    const responses = report.notices
      .map((n) => ({
        noticeId: n.noticeId,
        responseText: (texts[n.noticeId] ?? "").trim(),
      }))
      .filter((r) => r.responseText.length > 0);
    if (responses.length === 0) {
      toaster.error({
        title: "Réponse vide",
        description: "Renseignez au moins une réponse avant de générer",
      });
      return;
    }
    setSubmittingReportId(report.reportId);
    try {
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/report-responses",
        {
          params: { path: { yardId } },
          body: { reportId: report.reportId, responses },
        },
      );
      if (error || isNil(data)) {
        toaster.error({
          title: "Erreur",
          description: error?.error || "Échec de la génération",
        });
        return;
      }
      setGenerated((prev) => ({ ...prev, [report.reportId]: data.data.id }));
      toaster.success({
        title: "Réponse générée",
        description: "La réponse a été enregistrée",
      });
    } catch {
      toaster.error({ title: "Erreur", description: "Échec de la génération" });
    } finally {
      setSubmittingReportId(null);
    }
  };

  const download = (responseId: number) => {
    if (isNotNil(yardId)) downloadReportResponse(yardId, responseId);
  };

  const reports = state.status === "success" ? state.reports : [];
  const total = useMemo(
    () => reports.reduce((n, r) => n + r.notices.length, 0),
    [reports],
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Générer une réponse à rapport d'examen
        </h2>
        {state.status === "success" && (
          <p className="mt-1 text-sm text-zinc-500">
            {total} avis nécessitant une réponse
          </p>
        )}
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {state.status === "loading" ? (
            <p className="text-sm text-zinc-500">Chargement...</p>
          ) : reports.length === 0 ? (
            <p className="text-sm text-zinc-500">
              Aucune observation ne nécessite de réponse
            </p>
          ) : (
            <ul className="space-y-6">
              {reports.map((report) => {
                const responseId = generated[report.reportId];
                return (
                  <li
                    key={report.reportId}
                    className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                        Rapport {report.reportNumero ?? `#${report.reportId}`}
                      </h3>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          onClick={() => generate(report)}
                          disabled={isNotNil(submittingReportId)}
                        >
                          {submittingReportId === report.reportId
                            ? "Génération..."
                            : "Générer la réponse"}
                        </Button>
                        {!isNil(responseId) && (
                          <Button
                            type="button"
                            outline
                            onClick={() => download(responseId)}
                          >
                            Télécharger
                          </Button>
                        )}
                      </div>
                    </div>

                    <ul className="mt-3 space-y-4">
                      {report.notices.map((notice) => (
                        <li key={notice.noticeId}>
                          <p className="text-xs font-medium text-zinc-500">
                            Avis {notice.nAvis ?? `#${notice.noticeId}`}
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            {notice.observations.map((obs) => (
                              <li
                                key={obs.id}
                                className="text-xs text-zinc-600 dark:text-zinc-400"
                              >
                                • {obs.description}
                              </li>
                            ))}
                          </ul>
                          <label
                            htmlFor={`reponse-${notice.noticeId}`}
                            className="mt-2 block text-xs font-medium text-zinc-500"
                          >
                            Réponse
                          </label>
                          <textarea
                            id={`reponse-${notice.noticeId}`}
                            rows={3}
                            value={texts[notice.noticeId] ?? ""}
                            onChange={(e) =>
                              setTexts((prev) => ({
                                ...prev,
                                [notice.noticeId]: e.target.value,
                              }))
                            }
                            className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                          />
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
